import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const categoryArticles = articles.filter((a) => a.category === activeCategory);
  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeCategory]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">分类不存在</h1>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 白丝文化指南`,
    description: currentCategory.description,
    url: `https://baisi-style.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '白丝文化指南',
      url: 'https://baisi-style.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}｜白丝文化指南 - ${currentCategory.description.slice(0, 30)}`}
        description={currentCategory.description}
        keywords={`白丝,${currentCategory.name},白丝搭配,白丝风格,白丝美学,白丝文化`}
        canonicalUrl={`https://baisi-style.com/category/${activeCategory}`}
        schema={schema}
      />
      <Navbar />

      <main className="pt-16">
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="mt-4">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center space-x-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <div className="mb-6">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-700">{categoryArticles.length}</strong> 篇文章
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-article-line text-2xl text-secondary-500"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}